import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Sliders } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { createConfigAutosave, waitForConfigurationWrites } from "@/lib/configAutosave";


export default function Configuration() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.plan === "admin";
  const vipMulti = ["pro", "elite", "admin"].includes((user?.plan || "free").toLowerCase());
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [saveError, setSaveError] = useState("");
  const accountIdRef = useRef(localStorage.getItem("fishit_account") || "");
  const editorRef = useRef(null);

  useEffect(() => {
    let active = true;
    const requestConfig = { headers: { "X-Account-Id": accountIdRef.current } };
    (async () => {
      await waitForConfigurationWrites(accountIdRef.current);
      if (!active) return;
      const r = await api.get("/automation/config", requestConfig);
      if (!active) return;
      if (!accountIdRef.current && r.data?.user_id?.includes(":")) {
        accountIdRef.current = r.data.user_id.split(":").slice(1).join(":");
        requestConfig.headers["X-Account-Id"] = accountIdRef.current;
      }
      editorRef.current = createConfigAutosave({
        config: r.data, accountId: accountIdRef.current, api,
        onState: (state) => {
          if (!active) return;
          setCfg(state.cfg);
          setBusy(state.busy);
          setSaveStatus(state.status);
          setSaveError(state.error);
        },
      });
    })().catch((e) => {
      if (active) toast.error(e?.response?.data?.detail || "Gagal memuat konfigurasi");
    });
    const beforeSwitch = (event) => event.detail?.waitUntil?.(editorRef.current?.flush() ?? Promise.resolve(true));
    const beforeUnload = (event) => {
      if (!editorRef.current?.hasPending()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const visibilityChanged = () => {
      if (document.visibilityState === "hidden") void editorRef.current?.flush();
    };
    window.addEventListener("fishit-before-account-switch", beforeSwitch);
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("visibilitychange", visibilityChanged);
    return () => {
      active = false;
      window.removeEventListener("fishit-before-account-switch", beforeSwitch);
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("visibilitychange", visibilityChanged);
      void editorRef.current?.close();
      editorRef.current = null;
    };
  }, []);

  const update = (k, v) => editorRef.current?.update(k, v);

  const save = async () => {
    if (await editorRef.current?.flush()) toast.success("Konfigurasi sudah tersimpan.");
  };

  if (!cfg) return <div className="text-slate-500">Memuat...</div>;

  const resetConfig = async () => {
    if (!window.confirm("Reset semua pengaturan ke rekomendasi default?")) return;
    if (await editorRef.current?.reset()) toast.success("Konfigurasi direset dan tersimpan.");
  };

  return (
    <div className="space-y-8" data-testid="config-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-pink-500 mb-2">Konfigurasi</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold">Pengaturan bot</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Sesuaikan target grup/bot, waktu, dan fitur otomatis. Pola deteksi sudah diatur untuk
            <span className="text-white"> @fish_it_bot</span>.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={resetConfig}
            disabled={busy}
            variant="outline"
            data-testid="reset-config"
            className="rounded-md border-white/15 text-slate-300 hover:text-pink-400"
          >
            Reset ke rekomendasi
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="save-config"
            className="bg-pink-500 hover:bg-pink-600 text-black font-bold rounded-md glow-pink"
          >
            <FloppyDisk size={16} className="mr-2" />
            {busy ? "Menyimpan..." : "Simpan sekarang"}
          </Button>
        </div>
      </div>

      <nav className="config-section-nav flex gap-2 overflow-x-auto" aria-label="Lompat ke bagian pengaturan">
        {[
          ["target-mode", "Target & mode"], ["kecepatan-timing", "Waktu"],
          ["otomatis-harian", "Harian"],
          ["filter-chat-boost", "Mancing & boost"],
        ].map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
      </nav>

      <div
        data-testid="config-edit-warning"
        className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${saveStatus === "error" ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-cyan-500/30 bg-cyan-500/10 text-cyan-100"}`}
      >
        <div className="space-y-1">
          <p role="status" aria-live="polite" data-testid="config-save-status" className="font-semibold">
            {({ saved: "Tersimpan", pending: "Perubahan akan disimpan otomatis…", saving: "Menyimpan perubahan…", waiting: "Menunggu proses bot selesai", error: "Perubahan belum tersimpan di server" })[saveStatus]}
          </p>
          <p>Perubahan disimpan otomatis 2 detik setelah Anda selesai mengubah. Bot yang sedang berjalan akan melanjutkan dengan pengaturan baru. Akun Stop atau dijeda tetap tidak dijalankan.</p>
          {saveError && <p role={saveStatus === "error" ? "alert" : undefined}>{saveError}</p>}
        </div>
      </div>

      <Section title="Target & Mode" icon={<Sliders size={18} />}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Mode Automation">
            <Select value={cfg.mode} onValueChange={(v) => update("mode", v)}>
              <SelectTrigger data-testid="select-mode" className="bg-[#05050A] border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F16] border-white/10">
                <SelectItem value="vip_direct">VIP Direct (DM bot 5 menit)</SelectItem>
                <SelectItem value="group">Group (via grup + Daftar Mancing)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div />
          <Field label="Bot Fish It (username DM)">
            <Input
              data-testid="input-bot-username"
              value={cfg.bot_username || ""}
              onChange={(e) => update("bot_username", e.target.value)}
              placeholder="@fish_it_bot"
              className="bg-[#05050A] border-white/10 font-mono"
            />
          </Field>
          <Field label="Grup Target (untuk mode Group)">
            <Input
              data-testid="input-group-username"
              value={cfg.group_username || ""}
              onChange={(e) => update("group_username", e.target.value)}
              placeholder="@namagrup"
              className="bg-[#05050A] border-white/10 font-mono"
            />
          </Field>
        </div>
      </Section>

      <Section title="Kecepatan & Timing">
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Jeda sebelum JOIN" hint="Masukkan milidetik. 500 ms = 0,5 detik; 1000 ms = 1 detik. Nilai 0 tetap mengikuti pacing aman Telegram per akun.">
            <div className="relative">
              <Input
                type="number"
                min={0}
                max={60000}
                step={1}
                data-testid="group-join-delay-ms"
                value={cfg.group_join_delay_ms ?? 500}
                onChange={(e) => update("group_join_delay_ms", Number(e.target.value))}
                className="bg-[#05050A] border-white/10 font-mono pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">ms</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[0, 250, 500, 1000, 2000].map((ms) => (
                <button key={ms} type="button" onClick={() => update("group_join_delay_ms", ms)}
                  className={`rounded px-2 py-1 text-[11px] border ${cfg.group_join_delay_ms === ms ? "border-cyan-400 text-cyan-300 bg-cyan-400/10" : "border-white/10 text-slate-500 hover:text-slate-300"}`}>
                  {ms < 1000 ? `${ms} ms` : `${ms / 1000} dtk`}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Jeda setelah inventory" hint="0 ms = langsung ke pemeriksaan grup berikutnya setelah respons extract/jual selesai.">
            <div className="relative">
              <Input
                type="number"
                min={0}
                max={60000}
                step={1}
                data-testid="post-inventory-delay-ms"
                value={cfg.post_inventory_delay_ms ?? 0}
                onChange={(e) => update("post_inventory_delay_ms", Number(e.target.value))}
                className="bg-[#05050A] border-white/10 font-mono pr-12"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">ms</span>
            </div>
          </Field>
          <Field label="Durasi Fishing Grup · detik">
            <Input type="number" value={cfg.group_fish_seconds} onChange={(e) => update("group_fish_seconds", parseInt(e.target.value) || 0)} className="bg-[#05050A] border-white/10 font-mono" />
          </Field>
          <Field label="Durasi Fishing VIP · detik">
            <Input type="number" value={cfg.vip_fish_seconds} onChange={(e) => update("vip_fish_seconds", parseInt(e.target.value) || 0)} className="bg-[#05050A] border-white/10 font-mono" />
          </Field>
          <Field label="Jeda antar VIP · detik">
            <Input type="number" value={cfg.vip_gap_seconds} onChange={(e) => update("vip_gap_seconds", parseInt(e.target.value) || 0)} className="bg-[#05050A] border-white/10 font-mono" />
          </Field>
          <Field label="Sell tiap N fishing">
            <Input type="number" value={cfg.extract_sell_every_n_fish} onChange={(e) => update("extract_sell_every_n_fish", parseInt(e.target.value) || 1)} className="bg-[#05050A] border-white/10 font-mono" />
          </Field>
          <Field label="Item per halaman inventory">
            <Input type="number" value={cfg.inventory_page_size} onChange={(e) => update("inventory_page_size", parseInt(e.target.value) || 20)} className="bg-[#05050A] border-white/10 font-mono" />
          </Field>
        </div>
      </Section>

      <Section title="Otomatis Harian">
        <div className="grid md:grid-cols-2 gap-4">
          <ToggleCard
            label="Auto Donasi Clan"
            hint="ON: setiap 00:01 WIB donasi 10 juta koin melalui Fish It Market. Hanya berjalan jika level mancing minimal 500 dan akun sudah masuk clan."
            checked={!!cfg.auto_clan_donation}
            onChange={(v) => update("auto_clan_donation", v)}
            testid="switch-auto-clan-donation"
          />
          <ToggleCard
            label="Auto Beli Umpan"
            hint="Saat stok umpan tinggal 150 atau kurang, pakai stok yang sudah dimiliki dahulu; bila perlu, beli paket terbesar yang terjangkau (maksimal 250x) dalam satu transaksi. Harga, jenis umpan, dan saldo diperiksa sebelum membeli."
            checked={!!cfg.auto_bait_purchase}
            onChange={(v) => update("auto_bait_purchase", v)}
            testid="switch-auto-bait-purchase"
          />
        </div>
      </Section>


      <Section title="Filter Chat & Boost">
        {isAdmin && (
        <div className="grid md:grid-cols-2 gap-4">
          <Field
            label="Chat Tambahan yang Dibaca (pisahkan koma)"
            hint={
              vipMulti
                ? "Engine HANYA membaca pesan dari Bot, Grup Target, dan daftar ini. Chat/grup lain diabaikan — Activity Log jadi bersih."
                : "Paket Starter dibatasi 1 bot/grup saja. Upgrade ke Pro atau Elite untuk memakai banyak bot VIP."
            }
          >
            <Input
              data-testid="input-extra-allowed-chats"
              value={vipMulti ? (cfg.extra_allowed_chats || "") : ""}
              onChange={(e) => update("extra_allowed_chats", e.target.value)}
              disabled={!vipMulti}
              placeholder={vipMulti ? "@fish_it_vip_bot, @fish_it_vip3_bot" : "Khusus Pro/Elite"}
              className="bg-[#05050A] border-white/10 font-mono text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </Field>
          <Field label="Perintah Boost (DM Bot)">
            <Input
              data-testid="input-boost-command"
              value={cfg.boost_command || ""}
              onChange={(e) => update("boost_command", e.target.value)}
              placeholder="/boost"
              className="bg-[#05050A] border-white/10 font-mono"
            />
          </Field>
          <Field
            label="Trigger Boost DM (Regex)"
            hint='Kirim /boost ke bot saat teks ini muncul di DM bot: "AUTO MANCING DIMULAI!" atau "Pendaftaran Berhasil"'
          >
            <Input
              data-testid="input-boost-trigger"
              readOnly
              value={cfg.boost_trigger_pattern || ""}
              onChange={(e) => update("boost_trigger_pattern", e.target.value)}
              className="bg-[#05050A] border-white/10 font-mono text-xs"
            />
          </Field>
          <Field label="Perintah Boost Grup">
            <Input
              data-testid="input-group-boost-command"
              value={cfg.group_boost_command || ""}
              onChange={(e) => update("group_boost_command", e.target.value)}
              placeholder="/boost_grup"
              className="bg-[#05050A] border-white/10 font-mono"
            />
          </Field>
          <Field
            label="Trigger Boost Grup (Regex)"
            hint='Kirim /boost_grup ke grup saat teks ini muncul: "Boost Grup Berakhir!"'
          >
            <Input
              data-testid="input-group-boost-trigger"
              readOnly
              value={cfg.group_boost_trigger_pattern || ""}
              onChange={(e) => update("group_boost_trigger_pattern", e.target.value)}
              className="bg-[#05050A] border-white/10 font-mono text-xs"
            />
          </Field>
          <Field label="Cooldown Boost (detik)" hint="Durasi boost ±5 menit — tidak dikirim ulang selama cooldown">
            <Input
              type="number"
              data-testid="input-boost-cooldown"
              value={cfg.boost_cooldown_seconds ?? 300}
              onChange={(e) => update("boost_cooldown_seconds", parseInt(e.target.value) || 300)}
              className="bg-[#05050A] border-white/10 font-mono"
            />
          </Field>
        </div>
        )}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <ToggleCard
            label="Open Mancing"
            hint="ON: akun ini boleh /open_mancing (grup). OFF: tidak /open_mancing — TETAPI tetap JOIN saat 'PENDAFTARAN DIBUKA'."
            checked={cfg.open_mancing !== false}
            onChange={(v) => update("open_mancing", v)}
            testid="switch-open-mancing"
          />
          <ToggleCard
            label="Pin Pendaftaran"
            hint="ON: pin pesan 'PENDAFTARAN DIBUKA' dari bot beserta tombol Daftar Mancing. Akun Telegram harus punya izin pin di grup; join tetap berjalan jika pin gagal."
            checked={!!cfg.auto_pin_registration}
            onChange={(v) => update("auto_pin_registration", v)}
            testid="switch-auto-pin-registration"
          />
          <ToggleCard
            label="Boost Group"
            hint="ON: kirim boost grup ke GRUP saat 'Boost Grup Berakhir!'. Independen dari Boost Private."
            checked={!!cfg.boost_group}
            onChange={(v) => update("boost_group", v)}
            testid="switch-boost-group"
          />
          <ToggleCard
            label="Boost Private"
            hint="ON: kirim /boost ke DM bot saat AUTO MANCING DIMULAI atau Pendaftaran Berhasil. Independen dari Boost Group."
            checked={!!cfg.boost_private}
            onChange={(v) => update("boost_private", v)}
            testid="switch-boost-private"
          />
        </div>
      </Section>



      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={busy}
          className="bg-pink-500 hover:bg-pink-600 text-black font-bold rounded-md glow-pink"
        >
          <FloppyDisk size={16} className="mr-2" />
          Simpan Konfigurasi
        </Button>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div id={id} className="scroll-mt-24 border border-white/10 bg-[#0F0F16] rounded-2xl p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-6">
        {icon && <span className="text-pink-500">{icon}</span>}
        <h3 className="font-heading text-lg font-bold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs mb-2 block">{label}</Label>
      {children}
      {hint && <div className="text-[11px] text-slate-600 mt-1">{hint}</div>}
    </div>
  );
}

function ToggleCard({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#05050A] px-4 py-3">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} className="mt-0.5" />
      <div>
        <div className="text-sm text-slate-200 font-medium">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
      </div>
    </div>
  );
}
